import React, { useEffect, useState, useCallback, useRef, useContext } from 'react';
import { secureGet, secureDelete, securePost, api } from '../../config/api';
import { useTranslation } from 'react-i18next';
import { Helmet } from "react-helmet-async";
import { useHistory } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import { PrimaryButton } from '../../components/PrimaryButton';
import Table from '../../components/Table';
import Filter from '../../components/Filter';
import OptionsMenu from '../../components/OptionsMenu';
import DeletePopup from '../../components/DeletePopup';
import PageScroller from '../../components/PageScroller';
import TableLoadingArea from '../../components/TableLoadingArea';
import SnackbarNotification from '../../components/SnackbarNotification';
import UserContext from '../../Shared/UserContext';
import { formatDateTime } from '../../utils/format';
import EditIcon from '../../icons/edit.svg';
import BlockIcon from '../../icons/block.svg';
import LocatedIcon from '../../icons/located.svg';
import NotLocatedIcon from '../../icons/notlocated.svg';
import AddIcon from '../../icons/plusB.svg';
import CheckIcon from '../../icons/check.svg';

const CLIENT_STATUS_CHIP_COLORS = {
  LEAD: "#A8ADC3",
  TRIAL: "#0283F4",
  ACTIVE: "#06D6A0",
  SUSPENDED: "#EF476F",
  CANCELLED: "#38485C",
  EXPIRED: "#AFB1FF",
  TRIAL_EXPIRED: "#B43E8F",
};

const getClientStatusChipColor = (status) => {
  const key = String(status).toUpperCase();
  return CLIENT_STATUS_CHIP_COLORS[key] ?? "#EF476F";
};

const Clients = () => {
  const [clientsData, setClientsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ code: "", cityName: "", located: "", blocked: "" });
  const [pageNumber, setPageNumber] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [isDeletePopupVisible, setDeletePopupVisible] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });
  const { state: { role } } = useContext(UserContext);

  const history = useHistory();
  const { t } = useTranslation();
  const location = useLocation();
  const topRef = useRef(null);

  useEffect(() => {
    if (location.state?.showSnackbar) {
      showSnackbar(location.state.snackbarMessage, location.state.snackbarSeverity);
      history.replace({ ...location, state: undefined });
    }
  }, [location, history]);

  const showSnackbar = (message, severity = 'success', manual = false) => {
    setSnackbar({ open: true, message, severity, manual });
  };


const getOptions = (item) => {
  const status = (item?.grayClientStatus ?? '').toString().toUpperCase();
  const options = [
    { label: t(`actions.modify`), icon: EditIcon, action: () => handleModify(item) },
    status === 'SUSPENDED' && { label: t(`actions.activate`), icon: CheckIcon, action: () => handleActivate(item) },
  ];

  return options.filter(Boolean);
};


  const columns = [
    { label: t(`columns.clientName`), key: "companyName" },
    {
      label: t(`columns.registrationDate`),
      key: "createdAt",
      render: (date) => formatDateTime(date)
    },
    {
      label: t(`columns.grayClientId`),
      key: "grayClientId",
    },
    { label: t(`columns.phone`), key: "phoneNumber"},
    {
      label: t(`columns.status`),
      key: "grayClientStatus",
      render: (status) => (
        <span
          className="inline-flex items-center justify-center rounded-[12px] px-2 py-1 text-xs font-semibold text-white"
          style={{ backgroundColor: getClientStatusChipColor(status) }}
        >
          {status != null ? t(`clientStatus.${status}`) : "—"}
        </span>
      ),
    },
    {
      key: "actions",
      render: (_, rowData) => (
        <div onClick={(e) => e.stopPropagation()}>
          <OptionsMenu options={getOptions(rowData)} />
        </div>
      ),
    },
    //
  ];

  const filterOptions = [
    {
      name: "needle",
      type: "search",
    },
    {
      name: "status",
      label: t(`columns.status`),
      type: "dropdown",
      options: [
        { value: "LEAD", label: t(`clientStatus.LEAD`) },
        { value: "TRIAL", label: t(`clientStatus.TRIAL`) },
        { value: "ACTIVE", label: t(`clientStatus.ACTIVE`) },
        { value: "SUSPENDED", label: t(`clientStatus.SUSPENDED`) },
        { value: "CANCELLED", label: t(`clientStatus.CANCELLED`) },
        { value: "EXPIRED", label: t(`clientStatus.EXPIRED`) },
        { value: "TRIAL_EXPIRED", label: t(`clientStatus.TRIAL_EXPIRED`) },
      ],
    },
  ];

  const fetchClientsData = useCallback(async (customFilters = filters, page = pageNumber, size = pageSize) => {
    setLoading(true);
    const params = new URLSearchParams();
    params.append("page", page);
    params.append("size", size);
    if (customFilters.needle) params.append("needle", customFilters.needle);
    if (customFilters.status) params.append("grayClientStatus", customFilters.status);
    try {
      const { data } = await secureGet(`/grayClients?${params.toString()}`, showSnackbar);
      if (data) {
        setClientsData(data.content || []);
        console.log("Clients data:", data.content); 
        setTotalPages(data.totalPages || 1);
      }
      
    } catch (error) {
      console.error('Error fetching clients:', error);
      showSnackbar(t('errors.fetchClientsFailed'), 'error');
    } finally {
      setLoading(false);
    }
  }, [filters, pageNumber, pageSize, t]);

  useEffect(() => {
    const state = location.state || {};
    const newFilters = state.filters || { code: "", cityName: "", located: "", blocked: "" };
    const page = typeof state.page === "number" ? state.page : 0;
    const size = typeof state.size === "number" ? state.size : 100;

    setFilters(newFilters);
    setPageNumber(page);
    setPageSize(size);

    fetchClientsData(newFilters, page, size);
  }, []);

  const handleFilterChange = (filterName, filterValue) => {
    const updated = { ...filters, [filterName]: filterValue };
    setFilters(updated);
    setPageNumber(0);
    fetchClientsData(updated, 0, pageSize);

    history.replace({
      pathname: location.pathname,
      state: { filters: updated, page: 0, size: pageSize },
    });
  };

  const resetFilters = () => {
    const reset = { code: "", cityName: "", located: "", blocked: "" };
    setFilters(reset);
    setPageNumber(0);
    fetchClientsData(reset, 0, pageSize);

    history.replace({
      pathname: location.pathname,
      state: { filters: reset, page: 0, size: pageSize },
    });
  };

  const handleDeleteClick = (item) => {
    setItemToDelete(item);
    setDeletePopupVisible(true);
  };

  const handleModify = (client) => {
    history.push({
      pathname: `/clients/modify/${client.id}`,
      state: {
        initialValues: client,
        filters,
        page: pageNumber,
        size: pageSize,
      },
    });
  };


  const handleDeleteConfirm = async () => {
    if (!itemToDelete) return;
    try {
      const deleteUrl = `/clients/delete/${itemToDelete.id}`;
      await secureDelete(deleteUrl);

      setSnackbar({
        open: true,
        message: t(`confirmation.clientDelete`),
        severity: "success",
      });

      fetchClientsData();
    } catch (error) {
      console.error("Error deleting item:", error);
      setSnackbar({
        open: true,
        message: t('errors.deleteClientFailed'),
        severity: "error",
      });
    } finally {
      setDeletePopupVisible(false);
    }
  };

  const handleActivate = async (client) => {
    if (!client?.id) return;
    try {
      await securePost(`/grayClients/${client.id}/activate`, {}, showSnackbar);
      showSnackbar(t('actions.activate'), 'success');
      fetchClientsData();
    } catch (error) {
      console.error('Error activating client:', error);
      showSnackbar(t('errors.generic'), 'error');
    }
  };

  const handleAddClick = () => {
    history.push('/clients/add');
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 0 && newPage < totalPages) {
      setPageNumber(newPage);
      fetchClientsData(filters, newPage, pageSize);

      history.replace({
        pathname: location.pathname,
        state: { filters, page: newPage, size: pageSize },
      });

      if (topRef.current) topRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setPageNumber(0);
    fetchClientsData(filters, 0, newPageSize);

    history.replace({
      pathname: location.pathname,
      state: { filters, page: 0, size: newPageSize },
    });
  };

  const handleUpload = async (file) => {
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);

    try {

      const fileUploadResponse = await api.post(
        '/clients/import',
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            Authorization: localStorage.getItem('token'),
          },
        }
      );
      fetchClientsData();
      showSnackbar(t(`confirmation.upload`), 'success');

    } catch (error) {
      console.error("File upload failed:", error);
      showSnackbar(t('errors.generic'), 'error');
    }
  };

  return (
    <div ref={topRef}>
      <Helmet>
        <title>GrayFox - {t("titles.clients")}</title>
      </Helmet>
      <div className="flex items-center mb-[15px] justify-between">
        <h1 className="text-[40px] font-semibold">{t(`titles.clients`)}</h1>
        <div className="ml-auto text-[16px] flex gap-3">
          <PrimaryButton
            label={t(`primaryButtons.add_client`)}
            icon={AddIcon}
            onClick={handleAddClick}
          />
        </div>
      </div>

      <Filter
        filters={filterOptions}
        onFilterChange={handleFilterChange}
        resetFilters={resetFilters}
        filterValues={filters}
      />

      <TableLoadingArea loading={loading}>
        <Table
          columns={columns}
          data={clientsData}
          onRowClick={(row) => {
            history.push({
              pathname: `/clients/details/${row.id}`,
              state: { filters, page: pageNumber, size: pageSize },
            });
          }}
        />
      </TableLoadingArea>

      <PageScroller
        pageNumber={pageNumber}
        totalPages={totalPages}
        onPageChange={handlePageChange}
        pageSize={pageSize}
        onPageSizeChange={handlePageSizeChange}
      />

      {isDeletePopupVisible && (
        <DeletePopup
          open={isDeletePopupVisible}
          onCancel={() => setDeletePopupVisible(false)}
          onConfirm={handleDeleteConfirm}
        />
      )}

      <SnackbarNotification
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        manual={snackbar.manual}
      />
    </div>
  );
};

export default Clients;